import { FC, MouseEvent, useState } from 'react';

import { Camera, ChevronLeft, ChevronRight } from 'lucide-react';

import { OfferPhoto } from '../../types';
import { cn } from '../../utils';
import { Button } from '../ui/Button';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../ui/Dialog';

interface Props {
    photos: OfferPhoto[];
    compact?: boolean;
    className?: string;
}

const stopCardClick = (event: MouseEvent) => event.stopPropagation();

export const OfferPhotoGallery: FC<Props> = ({
    photos,
    compact = false,
    className,
}) => {
    const [selectedIndex, setSelectedIndex] = useState(0);

    if (photos.length === 0) return null;

    const selectedPhoto = photos[selectedIndex] ?? photos[0];
    const selectPrevious = () =>
        setSelectedIndex(
            (index) => (index - 1 + photos.length) % photos.length,
        );
    const selectNext = () =>
        setSelectedIndex((index) => (index + 1) % photos.length);

    return (
        <Dialog onOpenChange={(open) => open && setSelectedIndex(0)}>
            <DialogTrigger asChild>
                <Button
                    type="button"
                    variant="outline"
                    size={compact ? 'icon' : 'sm'}
                    className={cn(
                        compact
                            ? 'h-8 w-8 rounded-full bg-background/90 shadow'
                            : 'h-8 gap-1.5',
                        className,
                    )}
                    aria-label={`Переглянути фото продавця: ${photos.length}`}
                    onClick={stopCardClick}
                >
                    <Camera className="h-4 w-4" />
                    {compact ? (
                        <span className="sr-only">Фото продавця</span>
                    ) : (
                        <span>Фото · {photos.length}</span>
                    )}
                </Button>
            </DialogTrigger>
            <DialogContent
                className="max-h-[92vh] w-[calc(100%-2rem)] max-w-4xl overflow-y-auto p-4 sm:p-6"
                onClick={stopCardClick}
            >
                <DialogHeader>
                    <DialogTitle>Фото продавця</DialogTitle>
                </DialogHeader>

                <div className="relative flex min-h-64 items-center justify-center overflow-hidden rounded-xl bg-muted/40 sm:min-h-[32rem]">
                    <img
                        src={selectedPhoto.url}
                        alt={selectedPhoto.filename}
                        className="max-h-[70vh] w-full object-contain"
                    />
                    {photos.length > 1 && (
                        <>
                            <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                className="absolute left-2 h-9 w-9 rounded-full bg-background/90"
                                aria-label="Попереднє фото"
                                onClick={selectPrevious}
                            >
                                <ChevronLeft className="h-5 w-5" />
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                className="absolute right-2 h-9 w-9 rounded-full bg-background/90"
                                aria-label="Наступне фото"
                                onClick={selectNext}
                            >
                                <ChevronRight className="h-5 w-5" />
                            </Button>
                        </>
                    )}
                </div>

                {photos.length > 1 && (
                    <div className="grid grid-cols-5 gap-2 sm:grid-cols-8">
                        {photos.map((photo, index) => (
                            <button
                                key={photo.id}
                                type="button"
                                className={cn(
                                    'aspect-square overflow-hidden rounded-lg border-2 bg-muted',
                                    index === selectedIndex
                                        ? 'border-primary'
                                        : 'border-transparent',
                                )}
                                aria-label={`Показати фото ${index + 1}`}
                                onClick={() => setSelectedIndex(index)}
                            >
                                <img
                                    src={photo.thumbnail_url}
                                    alt=""
                                    className="h-full w-full object-cover"
                                />
                            </button>
                        ))}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};
